/**
 * ReportarProblema — Botón flotante + CHAT para que CUALQUIER usuario del portal
 * reporte errores, mejoras o requerimientos conversando (estilo Claude Code):
 *
 *   - El usuario escribe con sus palabras lo que pasó.
 *   - La IA (portal_chatReporte) extrae los datos, pide en el mismo chat lo que
 *     falte (comportamiento actual/esperado, cómo reproducir), y cuando tiene
 *     suficiente arma el reporte estándar.
 *   - Se pueden adjuntar capturas de pantalla en cualquier momento.
 *   - Al confirmar, portal_crearReporteIssue crea el issue en GitHub con todo el
 *     contexto técnico + logs + capturas.
 *
 * No hay formulario: todo se ingresa como texto en el chat.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { Bug, X, Send, Loader2, Paperclip, CheckCircle2, AlertTriangle, Sparkles, FileText, ListChecks, ChevronLeft, MessageSquare } from 'lucide-react';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { flushObsClient } from '../../services/obsClientLogger';
import styles from './ReportarProblema.module.css';

/** Handle imperativo: permite abrir el chat de reporte desde el top bar. */
export interface ReportarProblemaHandle { abrir: () => void }

type Origen = 'portal' | 'app-movil';

interface Msg { rol: 'user' | 'assistant'; texto: string }
interface Adjunto { nombre: string; dataUrl: string; esImagen: boolean }
interface Reporte {
  tipo: string; titulo: string; descripcion: string; pasosTexto: string;
  resultadoEsperado: string; resultadoActual: string; severidad: string;
}

const callChat = httpsCallable(functions, 'portal_chatReporte');
const callCrear = httpsCallable(functions, 'portal_crearReporteIssue');
const callListar = httpsCallable(functions, 'portal_listarMisReportes');
const callDetalle = httpsCallable(functions, 'portal_detalleReporte');

type Vista = 'chat' | 'lista' | 'detalle';

interface ResumenReporte {
  issueNumber: number; titulo: string; tipo: string; severidad: string; origen: string;
  creadoEn: string | null; estado: string; estadoRazon: string | null; comentarios: number;
  reporterNombre?: string | null; reporterRole?: string | null;
}
interface DetalleReporte extends ResumenReporte {
  descripcion: string; pasosTexto: string; resultadoEsperado: string; resultadoActual: string;
  moduloNombre: string; ruta: string;
  adjuntos: Array<{ nombre: string; url: string; esImagen: boolean }>;
  comentariosLista: Array<{ autor: string; cuerpo: string; fecha: string | null }>;
}

/** Etiqueta + estilo del estado del issue para el usuario. */
function estadoLabel(estado: string, razon: string | null): { txt: string; cls: string } {
  if (estado === 'open') return { txt: 'En revisión', cls: 'estadoOpen' };
  if (estado === 'closed') return razon === 'not_planned'
    ? { txt: 'Cerrado', cls: 'estadoDescartado' }
    : { txt: 'Resuelto', cls: 'estadoResuelto' };
  return { txt: 'Enviado', cls: 'estadoOpen' };
}

function fechaCorta(iso: string | null): string {
  if (!iso) return '';
  try { return new Intl.DateTimeFormat('es-PA', { dateStyle: 'medium' }).format(new Date(iso)); }
  catch { return ''; }
}

const SALUDO: Msg = {
  rol: 'assistant',
  texto: '¡Hola! Contame qué querés reportar: un error, una mejora o algo nuevo que necesitás. Escribilo con tus palabras y yo te hago las preguntas que falten. Podés adjuntar capturas con 📎.',
};

const TIPO_ES: Record<string, string> = { error: 'Error', mejora: 'Mejora', requerimiento: 'Requerimiento' };
const SEV_ES: Record<string, string> = { baja: 'Baja', media: 'Media', alta: 'Alta', critica: 'Crítica' };

/** Nombre legible del módulo a partir de la ruta actual. */
function moduloDesdeRuta(pathname: string): string {
  const mapa: Array<[RegExp, string]> = [
    [/^\/sesiones/, 'Sesiones / Dashboard'],
    [/^\/dashboard/, 'Dashboard'],
    [/^\/cola/, 'Cola de pacientes'],
    [/^\/consulta|^\/video|^\/atender/, 'Consulta / Videollamada'],
    [/^\/config\/salas/, 'Configuración de salas'],
    [/^\/salas/, 'Salas'],
    [/^\/historial/, 'Historial de consultas'],
    [/^\/admin\/eventos/, 'Monitor de eventos'],
    [/^\/admin\/config-ia/, 'Configuración de IA'],
    [/^\/admin\/pruebas/, 'Pruebas automatizadas'],
    [/^\/admin\/notificaciones|^\/campanas/, 'Notificaciones / Campañas'],
    [/^\/admin\/medicos|^\/medicos/, 'Gestión de médicos'],
    [/^\/admin/, 'Administración'],
    [/^\/perfil/, 'Perfil'],
  ];
  for (const [re, nombre] of mapa) if (re.test(pathname)) return nombre;
  return pathname === '/' ? 'Inicio' : pathname;
}

export const ReportarProblema = forwardRef<ReportarProblemaHandle>(function ReportarProblema(_props, ref) {
  const { user, role } = useAuth();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([SALUDO]);
  const [input, setInput] = useState('');
  const [enviandoChat, setEnviandoChat] = useState(false);
  const [reporte, setReporte] = useState<Reporte | null>(null);
  const [adjuntos, setAdjuntos] = useState<Adjunto[]>([]);
  const [origen, setOrigen] = useState<Origen>('portal');
  const [creando, setCreando] = useState(false);
  const [resultado, setResultado] = useState<{ url: string; number: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Vista: chat de reporte / lista "Mis reportes" / detalle de un reporte.
  const [vista, setVista] = useState<Vista>('chat');
  const [lista, setLista] = useState<ResumenReporte[]>([]);
  const [listaEsAdmin, setListaEsAdmin] = useState(false);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [detalle, setDetalle] = useState<DetalleReporte | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Posición arrastrable de la ventana (null = posición por defecto de CSS,
  // abajo-izquierda). Se puede mover para tomar capturas del portal debajo.
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragOffset = useRef<{ dx: number; dy: number } | null>(null);

  const moduloNombre = useMemo(() => moduloDesdeRuta(location.pathname), [location.pathname]);

  const contexto = useCallback(() => ({
    ruta: location.pathname,
    moduloNombre,
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    idioma: navigator.language,
    appVersion: (import.meta.env.VITE_APP_VERSION as string | undefined) || import.meta.env.MODE || '',
    urlCompleta: window.location.href,
  }), [location.pathname, moduloNombre]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, reporte, creando]);

  const reset = useCallback(() => {
    setMessages([SALUDO]); setInput(''); setReporte(null); setAdjuntos([]);
    setOrigen('portal'); setResultado(null); setError(null);
    setVista('chat'); setDetalle(null);
  }, []);
  const cerrar = useCallback(() => { setOpen(false); reset(); }, [reset]);

  /* ── Arrastrar la ventana por el encabezado ── */
  const onDragStart = useCallback((e: React.MouseEvent) => {
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffset.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    setPos({ x: rect.left, y: rect.top }); // fija en top/left para poder moverla
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onMove = (e: MouseEvent) => {
      if (!dragOffset.current) return;
      const w = panelRef.current?.offsetWidth ?? 400;
      const h = panelRef.current?.offsetHeight ?? 600;
      const x = Math.min(Math.max(0, e.clientX - dragOffset.current.dx), window.innerWidth - w);
      const y = Math.min(Math.max(0, e.clientY - dragOffset.current.dy), window.innerHeight - h);
      setPos({ x, y });
    };
    const onUp = () => { dragOffset.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [open]);

  /* ── Adjuntar archivos: capturas O documentos (archivo o pegar) ── */
  const adjuntar = useCallback((file: File | null | undefined) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('El archivo supera 10 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setAdjuntos((c) => [...c, {
          nombre: file.name || (file.type.startsWith('image/') ? 'captura.png' : 'archivo'),
          dataUrl: reader.result as string,
          esImagen: file.type.startsWith('image/'),
        }].slice(0, 8));
        setError(null);
      }
    };
    reader.readAsDataURL(file);
  }, []);

  // Pegar imágenes en cualquier parte mientras el chat está abierto (aunque el
  // foco no esté en el campo de texto) — así se puede mover la ventana, tomar la
  // captura y pegarla. El texto se sigue pegando normal (solo interceptamos imágenes).
  useEffect(() => {
    if (!open || resultado) return;
    const onDocPaste = (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData?.items ?? []).find((it) => it.type.startsWith('image/'));
      if (item) { e.preventDefault(); adjuntar(item.getAsFile()); }
    };
    document.addEventListener('paste', onDocPaste);
    return () => document.removeEventListener('paste', onDocPaste);
  }, [open, resultado, adjuntar]);

  /* ── Enviar un mensaje del chat ── */
  const enviarMensaje = useCallback(async () => {
    const texto = input.trim();
    // Se puede enviar solo con adjuntos (sin texto): en ese caso mandamos un
    // mensaje automático para que el asistente sepa que se adjuntó algo.
    if ((!texto && adjuntos.length === 0) || enviandoChat) return;
    const textoAEnviar = texto
      || (adjuntos.some((a) => !a.esImagen) ? 'Te adjunté un archivo para el reporte.' : 'Te adjunté una captura de pantalla.');
    setError(null);
    const nuevos = [...messages, { rol: 'user' as const, texto: textoAEnviar }];
    setMessages(nuevos);
    setInput('');
    setEnviandoChat(true);
    try {
      const res = await callChat({
        mensajes: nuevos.map((m) => ({ rol: m.rol, texto: m.texto })),
        contexto: contexto(),
        numCapturas: adjuntos.length,
      });
      const d = res.data as { estado: string; mensaje: string; reporte: Reporte | null };
      setMessages((prev) => [...prev, { rol: 'assistant', texto: d.mensaje }]);
      if (d.estado === 'listo' && d.reporte) setReporte(d.reporte);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'No pude procesar el mensaje.';
      setMessages((prev) => [...prev, { rol: 'assistant', texto: `⚠️ ${msg}` }]);
    } finally {
      setEnviandoChat(false);
    }
  }, [input, enviandoChat, messages, contexto, adjuntos.length]);

  /* ── Crear el issue ── */
  const crear = useCallback(async () => {
    if (!reporte || creando) return;
    setError(null); setCreando(true);
    try {
      await flushObsClient().catch(() => undefined);
      const res = await callCrear({
        reporte, origen, contexto: contexto(),
        adjuntos: adjuntos.map((a) => ({ nombre: a.nombre, contenido: a.dataUrl })),
      });
      const d = res.data as { ok: boolean; issueUrl: string; issueNumber: number };
      setResultado({ url: d.issueUrl, number: d.issueNumber });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear el reporte.');
    } finally {
      setCreando(false);
    }
  }, [reporte, creando, origen, contexto, adjuntos]);

  /* ── Mis reportes: lista + detalle ── */
  const abrirLista = useCallback(async () => {
    setVista('lista'); setDetalle(null); setError(null); setCargandoLista(true);
    try {
      const res = await callListar({});
      const d = res.data as { reportes: ResumenReporte[]; esAdmin?: boolean };
      setLista(d.reportes ?? []);
      setListaEsAdmin(!!d.esAdmin);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar tus reportes.');
    } finally {
      setCargandoLista(false);
    }
  }, []);

  const abrirDetalle = useCallback(async (issueNumber: number) => {
    setVista('detalle'); setError(null); setCargandoDetalle(true); setDetalle(null);
    try {
      const res = await callDetalle({ issueNumber });
      const d = res.data as Omit<DetalleReporte, 'comentariosLista'> & { comentarios: DetalleReporte['comentariosLista'] };
      setDetalle({ ...d, comentariosLista: d.comentarios ?? [] } as DetalleReporte);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el reporte.');
      setVista('lista');
    } finally {
      setCargandoDetalle(false);
    }
  }, []);

  useImperativeHandle(ref, () => ({ abrir: () => setOpen(true) }), []);

  if (!user) return null;

  return (
    <>
      {open && (
        <div
          ref={panelRef}
          className={styles.panel}
          role="dialog"
          aria-label="Reportar un problema"
          style={pos ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : undefined}
        >
          <div className={styles.head} onMouseDown={onDragStart} title="Arrastrá para mover la ventana">
            <div className={styles.headTitle}>
              {vista === 'chat' ? (
                <span className={styles.headIcon}><Bug size={16} /></span>
              ) : (
                <button
                  type="button" className={styles.headBack}
                  onClick={() => (vista === 'detalle' ? abrirLista() : setVista('chat'))}
                  onMouseDown={(e) => e.stopPropagation()}
                  aria-label="Volver"
                ><ChevronLeft size={18} /></button>
              )}
              <div>
                <span className={styles.headName}>
                  {vista === 'lista' ? (listaEsAdmin ? 'Reportes abiertos' : 'Mis reportes') : vista === 'detalle' ? `Reporte #${detalle?.issueNumber ?? ''}` : 'Reportar'}
                </span>
                <span className={styles.headSub}>{vista === 'chat' ? moduloNombre : 'MinSalud'}</span>
              </div>
            </div>
            <div className={styles.headActions}>
              {vista === 'chat' && !resultado && (
                <button
                  type="button" className={styles.closeBtn}
                  onClick={abrirLista} onMouseDown={(e) => e.stopPropagation()}
                  title="Ver mis reportes" aria-label="Ver mis reportes"
                ><ListChecks size={18} /></button>
              )}
              <button type="button" className={styles.closeBtn} onClick={cerrar} onMouseDown={(e) => e.stopPropagation()} aria-label="Cerrar"><X size={18} /></button>
            </div>
          </div>

          {/* ── Mis reportes: lista ── */}
          {vista === 'lista' ? (
            <div className={styles.body}>
              {cargandoLista ? (
                <div className={styles.centerState}><Loader2 size={28} className={styles.spin} /><p>Cargando tus reportes…</p></div>
              ) : error ? (
                <div className={styles.errorBox}><AlertTriangle size={14} /> {error}</div>
              ) : lista.length === 0 ? (
                <div className={styles.centerState}>
                  <ListChecks size={34} />
                  <p className={styles.help}>Todavía no tenés reportes. Cuando crees uno, aparecerá acá con su estado.</p>
                  <button type="button" className={styles.primaryBtn} onClick={() => setVista('chat')}><MessageSquare size={16} /> Nuevo reporte</button>
                </div>
              ) : (
                lista.map((r) => {
                  const est = estadoLabel(r.estado, r.estadoRazon);
                  return (
                    <button key={r.issueNumber} type="button" className={styles.reporteItem} onClick={() => abrirDetalle(r.issueNumber)}>
                      <div className={styles.reporteItemTop}>
                        <span className={`${styles.estadoBadge} ${styles[est.cls]}`}>{est.txt}</span>
                        <span className={styles.reporteItemNum}>#{r.issueNumber}</span>
                      </div>
                      <span className={styles.reporteItemTitulo}>{r.titulo}</span>
                      <div className={styles.reporteItemMeta}>
                        <span>{TIPO_ES[r.tipo] || r.tipo}</span>
                        <span>·</span>
                        <span>{fechaCorta(r.creadoEn)}</span>
                        {listaEsAdmin && r.reporterNombre && <><span>·</span><span>{r.reporterNombre}</span></>}
                        {r.comentarios > 0 && <><span>·</span><span>{r.comentarios} comentario(s)</span></>}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          ) : vista === 'detalle' ? (
            <div className={styles.body}>
              {cargandoDetalle || !detalle ? (
                <div className={styles.centerState}><Loader2 size={28} className={styles.spin} /><p>Cargando el reporte…</p></div>
              ) : (
                <div className={styles.detalle}>
                  {(() => { const e = estadoLabel(detalle.estado, detalle.estadoRazon); return (
                    <div className={styles.detalleTags}>
                      <span className={`${styles.estadoBadge} ${styles[e.cls]}`}>{e.txt}</span>
                      <span className={styles.tag}>{TIPO_ES[detalle.tipo] || detalle.tipo}</span>
                      <span className={styles.tag}>Severidad: {SEV_ES[detalle.severidad] || detalle.severidad}</span>
                    </div>
                  ); })()}
                  <h3 className={styles.detalleTitulo}>{detalle.titulo}</h3>
                  <p className={styles.detalleMeta}>
                    {detalle.moduloNombre || detalle.ruta} · {fechaCorta(detalle.creadoEn)}
                    {detalle.reporterNombre && ` · Reportado por ${detalle.reporterNombre}`}
                  </p>

                  <div className={styles.detalleSec}><span className={styles.detalleLabel}>Descripción</span><p>{detalle.descripcion || '—'}</p></div>
                  {detalle.pasosTexto && <div className={styles.detalleSec}><span className={styles.detalleLabel}>Pasos para reproducir</span><p>{detalle.pasosTexto}</p></div>}
                  {detalle.resultadoEsperado && <div className={styles.detalleSec}><span className={styles.detalleLabel}>Resultado esperado</span><p>{detalle.resultadoEsperado}</p></div>}
                  {detalle.resultadoActual && <div className={styles.detalleSec}><span className={styles.detalleLabel}>Resultado actual</span><p>{detalle.resultadoActual}</p></div>}

                  {detalle.adjuntos.length > 0 && (
                    <div className={styles.detalleSec}>
                      <span className={styles.detalleLabel}>Adjuntos</span>
                      <div className={styles.detalleAdjuntos}>
                        {detalle.adjuntos.map((a, i) => a.esImagen
                          ? <a key={i} href={a.url} target="_blank" rel="noreferrer" className={styles.detalleImg}><img src={a.url} alt={a.nombre} /></a>
                          : <a key={i} href={a.url} target="_blank" rel="noreferrer" className={styles.detalleDoc}><FileText size={14} /> {a.nombre}</a>)}
                      </div>
                    </div>
                  )}

                  <div className={styles.detalleSec}>
                    <span className={styles.detalleLabel}>Seguimiento del equipo</span>
                    {detalle.comentariosLista.length === 0 ? (
                      <p className={styles.detalleVacio}>Sin comentarios todavía. Acá verás las respuestas del equipo.</p>
                    ) : (
                      detalle.comentariosLista.map((c, i) => (
                        <div key={i} className={styles.comentario}>
                          <div className={styles.comentarioHead}>{c.autor} · {fechaCorta(c.fecha)}</div>
                          <p>{c.cuerpo}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : resultado ? (
            <div className={styles.body}>
              <div className={styles.centerState}>
                <CheckCircle2 size={40} className={styles.okIcon} />
                <p className={styles.okTitle}>¡Reporte enviado!</p>
                <p className={styles.help}>Se registró tu reporte <strong>#{resultado.number}</strong> con toda la información. Podés seguir su estado en <strong>Mis reportes</strong>.</p>
                <div className={styles.footerBtns}>
                  <button type="button" className={styles.secondaryBtn} onClick={reset}>Reportar otro</button>
                  <button type="button" className={styles.primaryBtn} onClick={abrirLista}>Ver mis reportes</button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* ── Chat ── */}
              <div className={styles.body} ref={bodyRef}>
                {messages.map((m, i) => (
                  <div key={i} className={`${styles.msg} ${m.rol === 'user' ? styles.msgUser : styles.msgBot}`}>
                    {m.texto}
                  </div>
                ))}
                {enviandoChat && (
                  <div className={`${styles.msg} ${styles.msgBot} ${styles.typing}`}>
                    <Loader2 size={14} className={styles.spin} /> Escribiendo…
                  </div>
                )}

                {/* Adjuntos (capturas + documentos) */}
                {adjuntos.length > 0 && (
                  <div className={styles.shots}>
                    {adjuntos.map((a, i) => (
                      <div key={i} className={a.esImagen ? styles.shot : styles.shotDoc} title={a.nombre}>
                        {a.esImagen
                          ? <img src={a.dataUrl} alt={a.nombre} />
                          : <span className={styles.docChip}><FileText size={18} /><span className={styles.docName}>{a.nombre}</span></span>}
                        <button type="button" className={styles.shotDel} onClick={() => setAdjuntos((cs) => cs.filter((_, idx) => idx !== i))} aria-label="Quitar adjunto"><X size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Tarjeta de confirmación cuando el reporte está listo */}
                {reporte && (
                  <div className={styles.reporteCard}>
                    <div className={styles.reporteHead}>
                      <Sparkles size={14} /> Reporte listo para enviar
                    </div>
                    <div className={styles.reporteTags}>
                      <span className={styles.tag}>{TIPO_ES[reporte.tipo] || reporte.tipo}</span>
                      <span className={styles.tag}>Severidad: {SEV_ES[reporte.severidad] || reporte.severidad}</span>
                    </div>
                    <p className={styles.reporteTitulo}>{reporte.titulo}</p>
                    <div className={styles.origenRow}>
                      <span>¿Dónde ocurre?</span>
                      <div className={styles.origenBtns}>
                        <button type="button" className={origen === 'portal' ? styles.origenOn : styles.origenOff} onClick={() => setOrigen('portal')}>💻 Portal</button>
                        <button type="button" className={origen === 'app-movil' ? styles.origenOn : styles.origenOff} onClick={() => setOrigen('app-movil')}>📱 App móvil</button>
                      </div>
                    </div>
                    <button type="button" className={styles.primaryBtn} onClick={crear} disabled={creando}>
                      {creando ? <><Loader2 size={16} className={styles.spin} /> Creando reporte…</> : <>Crear reporte</>}
                    </button>
                    <span className={styles.reporteHint}>Se adjuntará tu usuario ({role}), la página, el navegador, los logs recientes{adjuntos.length ? ` y ${adjuntos.length} archivo(s)` : ''}.</span>
                  </div>
                )}

                {error && <div className={styles.errorBox}><AlertTriangle size={14} /> {error}</div>}
              </div>

              {/* ── Barra de entrada ── */}
              <div className={styles.inputBar}>
                <button type="button" className={styles.attachIcon} onClick={() => fileRef.current?.click()} title="Adjuntar captura o documento" aria-label="Adjuntar captura o documento">
                  <Paperclip size={18} />
                </button>
                <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.txt,.log,.csv,.xlsx,.json" hidden onChange={(e) => { adjuntar(e.target.files?.[0]); e.target.value = ''; }} />
                <textarea
                  className={styles.chatInput}
                  rows={1}
                  placeholder="Escribí tu mensaje…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarMensaje(); } }}
                  disabled={enviandoChat}
                />
                <button type="button" className={styles.sendBtn} onClick={enviarMensaje} disabled={enviandoChat || (!input.trim() && adjuntos.length === 0)} aria-label="Enviar">
                  <Send size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
});
